// external imports
import _ from 'lodash';

// shared imports
import ItemType, {ItemTypeAttributes} from '~/shared/src/mm/ItemType';
import PropertySet, {PropertySetAttributes} from '~/shared/src/mm/PropertySet';
import PropertyType, {PropertyTypeAttributes} from '~/shared/src/mm/PropertyType';
import ReferencePropertySet, {ReferencePropertySetAttributes} from '~/shared/src/mm/ReferencePropertySet';
import ReferenceSet, {ReferenceSetAttributes} from '~/shared/src/mm/ReferenceSet';
import ReferenceType, {ReferenceTypeAttributes} from '~/shared/src/mm/ReferenceType';
import Status, {StatusAttributes} from '~/shared/src/mm/Status';
import Value, {ValueAttributes} from '~/shared/src/mm/Value';
import ValueSet, {ValueSetAttributes} from '~/shared/src/mm/ValueSet';
import {negateIf} from '~/shared/src/utilities/logical';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';

// frontend imports

// backend imports
///////////////////////////
/////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
////////
////////////
//////////
////////
////////////////////////////////////////
//////////


type TableObjects = {
    itemType : ItemType,
    propertySet : PropertySet,
    propertyType : PropertyType,
    referencePropertySet : ReferencePropertySet,
    referenceSet : ReferenceSet,
    referenceType : ReferenceType,
    status : Status,
    value : Value,
    valueSet : ValueSet
};

type TableFields = {
    itemType : ItemTypeAttributes,
    propertySet : PropertySetAttributes,
    propertyType : PropertyTypeAttributes,
    referencePropertySet : ReferencePropertySetAttributes,
    referenceSet : ReferenceSetAttributes,
    referenceType : ReferenceTypeAttributes,
    status : StatusAttributes,
    value : ValueAttributes,
    valueSet : ValueSetAttributes
};

type TableData = {
    [tableName in keyof TableFields] : (
        { id : Map<number, TableObjects[tableName]> }
        & { [index in keyof Omit<TableFields[tableName], 'id'>]? : Map<any, TableObjects[tableName] | TableObjects[tableName][]> }
        )
};

type MetaModelContent = {
    itemType : TableFields['itemType'][],
    propertySet : TableFields['propertySet'][],
    propertyType : TableFields['propertyType'][],
    referencePropertySet : TableFields['referencePropertySet'][],
    referenceSet : TableFields['referenceSet'][],
    referenceType : TableFields['referenceType'][],
    status : TableFields['status'][],
    value : TableFields['value'][],
    valueSet : TableFields['valueSet'][]
};

type ImageData = {
    [tableName in keyof TableFields]? : {
        [ key : string ] : {
            [ type : string ] : string;
        };
    };
};

type MessageData = {
    [tableName in keyof TableFields]? : {
        [ key : string ] : {
            [ type : string ] : string;
        };
    };
};

type MetaModelData = {
    data : MetaModelContent;
    imageData? : ImageData;
    messageData? : MessageData;
};

type Table = keyof TableFields;

type TableIndex<K extends keyof TableFields> = keyof TableFields[K] | {
    name : keyof TableFields[K],
    type : 'multiple' | 'single'
};

type TablesWithIndices = {
    [tableName in keyof TableFields]? : ( TableIndex<tableName> )[];
};

type TableToCfgItemTypeMapping = {
    [tableName in keyof TableFields]? : string;
};

type TableImages = {
    [tableName in keyof TableFields]? : string[];
};

type TableMessages = {
    [tableName in keyof TableFields]? : string[];
};

export type MetaModelMockupData = {
    [schema in Schema]? : {
        [table in keyof MetaModelContent]? : MetaModelContent[ table ]
    }
};


export class MetaModel {

    /**
     * The schema to load the metamodel from
     */
    readonly schema : Schema;

    /**
     * The timestamp where the data was loaded from database
     */
    private timestamp : number = 0;

    /**
     * The data of the metaModel
     */
    private data : TableData = {
        itemType            : {
            id  : new Map<number, TableObjects['itemType']>(),
            name: new Map<string, TableObjects['itemType']>()
        },
        propertySet         : {
            id            : new Map<number, TableObjects['propertySet']>(),
            itemTypeId    : new Map<number, TableObjects['propertySet'][]>(),
            propertyTypeId: new Map<number, TableObjects['propertySet'][]>()
        },
        propertyType        : {
            id  : new Map<number, TableObjects['propertyType']>(),
            name: new Map<string, PropertyType>()
        },
        referencePropertySet: {
            id            : new Map<number, TableObjects['referencePropertySet']>(),
            referenceSetId: new Map<number, TableObjects['referencePropertySet'][]>(),
            propertyTypeId: new Map<number, TableObjects['referencePropertySet'][]>()
        },
        referenceSet        : {
            id             : new Map<number, TableObjects['referenceSet']>(),
            srcItemTypeId  : new Map<number, TableObjects['referenceSet'][]>(),
            destItemTypeId : new Map<number, TableObjects['referenceSet'][]>(),
            referenceTypeId: new Map<number, TableObjects['referenceSet'][]>()
        },
        referenceType       : {
            id  : new Map<number, TableObjects['referenceType']>(),
            name: new Map<string, TableObjects['referenceType'][]>()
        },
        status              : {
            id  : new Map<number, TableObjects['status']>(),
            name: new Map<string, TableObjects['status']>()
        },
        value               : {
            id        : new Map<number, TableObjects['value']>(),
            valueSetId: new Map<number, TableObjects['value'][]>()
        },
        valueSet            : {
            id: new Map<number, TableObjects['valueSet']>()
        }
    };

    get hasData() : boolean {
        const tables = Object.keys( this.data ) as Table[];

        let retVal : boolean = false;

        for ( const table of tables ) {
            if ( this.data[ table ].id?.size ) {
                retVal = true;
                break;
            }
        }

        return retVal;
    }

    /**
     * The tables to load metaModel data from in the order to be loaded with the indices to create therefore
     */
    private tablesInOrderToLoadWithIndices : TablesWithIndices = {
        status              : [ 'name' ],
        itemType            : [ 'name' ],
        propertyType        : [ 'name' ],
        referenceType       : [ 'name' ],
        valueSet            : [ 'name' ],
        value               : [ { name: 'valueSetId', type: 'multiple' } ],
        propertySet         : [ { name: 'itemTypeId', type: 'multiple' }, { name: 'propertyTypeId', type: 'multiple' } ],
        referencePropertySet: [ { name: 'referenceSetId', type: 'multiple' }, { name: 'propertyTypeId', type: 'multiple' } ],
        referenceSet        : [ { name: 'srcItemTypeId', type: 'multiple' }, { name: 'destItemTypeId', type: 'multiple' }, { name: 'referenceTypeId', type: 'multiple' } ]
    };

    private tableToCfgItemTypeMapping : TableToCfgItemTypeMapping = {
        itemType            : 'YnItemType',
        propertySet         : 'YnPropertySet',
        referenceSet        : 'YnReferenceSet',
        referencePropertySet: 'YnReferencePropertySet',
        valueSet            : 'YnValueSet',
        value               : 'YnValue'
    };

    private imageData : ImageData = {};

    private tableImages : TableImages = {
        itemType: [ 'isIconFor' ]
    };

    private messageData : MessageData = {};

    private tableMessages : TableMessages = {
        itemType            : [ 'isTypeLabelFor', 'isKeyLabelFor', 'isNameLabelFor' ],
        propertySet         : [ 'isTypeLabelFor', 'isKeyLabelFor', 'isValueLabelFor' ],
        referenceSet        : [ 'isTypeLabelFor' ],
        referencePropertySet: [ 'isTypeLabelFor', 'isKeyLabelFor', 'isValueLabelFor' ],
        value               : [ 'isLabelFor' ]
    };

    constructor( schema : Schema ) {
        this.schema = schema;
    }

///////////////////////////////////////////
////////////////////////
/////////////
//////////////////////////////////////
/////////
////////////////////////////
//////////////////////////////
/////////
/////////////////
////////////////////////////////////////
/////////
/////

///////////////////////////////////////
/////////////
/////////////////////////////////////////////////////
/////////
////////////////////////////
//////////////////////////////
/////////
/////

//////////////////////////////////
///////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////
///////////////////////////////////////
///////////////////////////////////////////////////////////
//////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
//////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////
//////////////////////////////////////////////////////////////////

/////////////////////////
////////////////////////////////////////////////////////
/////////////
/////////

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////

/////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////
//////////////////////////////////////////////
//////////////////////////////////////////

/////////////////////////////////////////////////////////////////
//////////////////////////////////
////////////////////////////////////
//////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////

///////////////////////////////////////
/////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////

////////////////////////////////////////
//////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////

////////////////////////////////////////////////
//////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////

/////////////////////////////////
///////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////
/////////////////
/////////////
/////////

//////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////

/////////////////////////////////
////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////

/////////////////////////
///////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////////////////////////////////////////////////////

/////////////////////////////////////////////
///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////
/////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////
//////////////////////////////////
/////////////////////////////
/////////////////////////
/////////////////////
/////////////////////////////////
/////////////////////////////////
/////////////////////
/////////////////////////////
//////////////////////////////////////////////////////////
/////////////////////
/////////////////
/////////////
/////////

///////////////////////////////////
//////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////

/////////////////////////
///////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////////////////////////////////////////////////////

/////////////////////////////////////////////
///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////
//////////////////////////////////
/////////////////////////////
/////////////////////////
/////////////////////
/////////////////////////////////
/////////////////////////////////
/////////////////////
/////////////////////////////
//////////////////////////////////////////////////////////
/////////////////////
/////////////////
/////////////
/////////

////////////////////////////////////////////
//////////////////////////////////////////////////
////////////////////////////////////////////////////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////

//////////////

    loadMockupData( mockupData : MetaModelMockupData ) {
        try {
            this.doLoadMockupData( mockupData );
        }
        catch ( errorMsg ) {
            error( errorMsg );
        }
        finally {
            this.timestamp = Date.now();
        }
    }

    doLoadMockupData( mockupData : MetaModelMockupData ) {
        if ( mockupData[ this.schema ] ) {
            const schemaMockupData = mockupData[ this.schema ]!,
                  tables           = Object.keys( schemaMockupData ) as Table[];

            for ( const table of tables ) {
                this.insertDataIntoTable( table, schemaMockupData[ table ]! );
            }
        }
    }

    /**
     * Returns a JSON string with the content of this.
     */
    toJsonString() : string {
        const retVal : MetaModelData = {
                  data       : {
                      itemType            : [],
                      propertySet         : [],
                      propertyType        : [],
                      referencePropertySet: [],
                      referenceSet        : [],
                      referenceType       : [],
                      status              : [],
                      value               : [],
                      valueSet            : []
                  },
                  imageData  : this.imageData,
                  messageData: this.messageData
              },
              tables                 = Object.keys( retVal.data ) as Table[];

        for ( const table of tables ) {
            for ( const value of this.data[ table ].id.values() ) {
                retVal.data[ table ].push( ( value as any ).toJSON() );
            }
        }

        return JSON.stringify( retVal );
    }

    /**
     * Loads the content for this given by JSON string.
     *
     * @param {string} jsonData
     * @param {number} [timestamp=Date.now()]    The creation time of the data
     */
    fromJsonString( jsonData : string, timestamp : number = Date.now() ) {
        try {
            const { data, imageData, messageData } = <MetaModelData> JSON.parse( jsonData );

            if ( data ) {
                const tables = Object.keys( this.tablesInOrderToLoadWithIndices ) as Table[];

                for ( const table of tables ) {
                    this.insertDataIntoTable( table, data[ table ] );
                }
            }

            if ( imageData ) {
                this.imageData = imageData;
            }

            if ( messageData ) {
                this.messageData = messageData;
            }
        }
        catch ( errorMsg ) {
            error( errorMsg );
        }
        finally {
            this.timestamp = timestamp;
        }

///////////////////////////////////
///////////////////////////////////
/////////////////////////////////////////////////////////////////////
/////////////////////
/////////////////////////////////////////////////////////////////////
//////////////////
    }

    /**
     * Getter for timestamp
     */
    getTimestamp() : number {
        return this.timestamp;
    }

    getImage( table : keyof TableFields, id : number, type : string ) {
        return this.imageData?.[ table ]?.[ id ]?.[ type ];
    }

    getMessage( table : keyof TableFields, id : number, type : string ) {
        return this.messageData?.[ table ]?.[ id ]?.[ type ];
    }

    private insertDataIntoTable<K extends Table>( table : K, data : TableFields[ K ][] ) {
        const tableIndices = this.tablesInOrderToLoadWithIndices[ table ] ?? [];

        // initialize id index for table
        this.data[ table ][ 'id' ] = new Map() as unknown as TableData[ K ][ 'id' ];

        // initialize indices for table
        for ( const index of tableIndices ) {
            const indexName = ( typeof index === 'object' ? index.name : index ) as keyof TableData[ K ];

            this.data[ table ][ indexName ] = new Map() as unknown as TableData[ K ][ keyof TableData[ K ] ];
        }

        for ( const dataRow of data ) {
            try {
                const object = this.createObjectFromDataFor( table, dataRow );

                this.data[ table ].id.set( object.id, object );

                // add indices for table object
                for ( const index of tableIndices ) {
                    const indexName     = ( typeof index === 'object' ? index.name : index ) as keyof TableData[ K ],
                          attributeName = indexName as keyof TableObjects[ K ],
                          type          = typeof index === 'object' ? index.type : 'single',
                          indexMap      = this.data[ table ][ indexName ]!; // is defined!

                    if ( type === 'single' ) {
                        indexMap.set( object[ attributeName ], object );
                    }
                    else {
                        if ( !indexMap.has( object[ attributeName ] ) ) {
                            indexMap.set( object[ attributeName ], [] );
                        }

                        ( indexMap.get( object[ attributeName ] ) as TableObjects[K][] ).push( object );
                    }
                }
            }
            catch ( e ) {
                warn( e + ` (${dataRow.uuid})` );
            }
        }
    }

    /**
     * Creates the object for the given type by given data.
     *
     * ATTENTION: this function must be called with a full set of attributes for the objects type, or the returned data might be invalid.
     */
    private createObjectFromDataFor<K extends Table>( type : K, attributes : TableFields[ K ] ) : TableObjects[ K ] {
        let retVal : any;

        switch ( type ) {
            case 'itemType':
                retVal = new ItemType( attributes as ItemTypeAttributes, this.schema );
                break;
            case 'propertySet':
                retVal = new PropertySet( attributes as PropertySetAttributes, this.schema );
                break;
            case 'propertyType':
                retVal = new PropertyType( attributes as PropertyTypeAttributes, this.schema );
                break;
            case 'referencePropertySet':
                retVal = new ReferencePropertySet( attributes as ReferencePropertySetAttributes, this.schema );
                break;
            case 'referenceSet':
                retVal = new ReferenceSet( attributes as ReferenceSetAttributes, this.schema );
                break;
            case 'referenceType':
                retVal = new ReferenceType( attributes as ReferenceTypeAttributes, this.schema );
                break;
            case 'status':
                // TODO: remove, when physically deleted is not used anymore
                if ( ( attributes as StatusAttributes ).name === 'physically deleted' ) {
                    ( attributes as StatusAttributes ).name = 'deleted';
                }
                retVal = new Status( attributes as StatusAttributes, this.schema );
                break;
            case 'value':
                retVal = new Value( attributes as ValueAttributes, this.schema );
                break;
            case 'valueSet':
                retVal = new ValueSet( attributes as ValueSetAttributes, this.schema );
                break;
        }

        return retVal;
    }


    /************************************************************
     * ItemType
     ************************************************************/

    /**
     * Returns all ItemTypes
     */
    getItemTypes() : ItemType[] {
        return [ ...this.data.itemType.id.values() ];
    }

    /**
     * Returns the ItemType for given itemType
     *
     * @param {Number|string} itemType      The itemTypeId or itemTypeName
     */
    getItemType( itemType : number | string ) : ItemType | undefined {
        let retVal;

        if ( typeof itemType === 'number' && this.data.itemType.id.has( itemType ) ) {
            retVal = this.data.itemType.id.get( itemType );
        }
        else if ( typeof itemType === 'string' && this.data.itemType.name?.has( itemType ) ) {
            retVal = this.data.itemType.name.get( itemType );

            if ( retVal ) {
                retVal = retVal as ItemType;
            }
        }

        return retVal;
    }


    /************************************************************
     * PropertySet
     ************************************************************/

    /**
     * Returns all PropertySets
     */
    getPropertySets() : PropertySet[] {
        return [ ...this.data.propertySet.id.values() ];
    }

    /**
     * Returns the PropertySet for given propertySetId
     *
     * @param {Number} propertySetId
     */
    getPropertySet( propertySetId : number ) : PropertySet | undefined {
        let retVal;

        if ( this.data.propertySet.id.has( propertySetId ) ) {
            retVal = this.data.propertySet.id.get( propertySetId );
        }

        return retVal;
    }

    /**
     * Returns all PropertySets for given itemType
     *
     * @param {Number|string} itemType      The itemTypeId or itemTypeName
     */
    getPropertySetsForItemType( itemType : number | string ) : PropertySet[] {
        let retVal : PropertySet[] = [];

        const itemTypeId = this.getItemType( itemType )?.id;

        if ( itemTypeId && this.data.propertySet.itemTypeId?.has( itemTypeId ) ) {
            retVal = this.data.propertySet.itemTypeId.get( itemTypeId ) as PropertySet[];
        }

        return retVal;
    }

    /**
     * Returns all PropertySets for given propertyType
     *
     * @param {Number|string} propertyType      The propertyTypeId or propertyTypeName
     */
    getPropertySetsForPropertyType( propertyType : number | string ) : PropertySet[] {
        let retVal : PropertySet[] = [];

        const propertyTypeId = this.getPropertyType( propertyType )?.id;

        if ( propertyTypeId && this.data.propertySet.propertyTypeId?.has( propertyTypeId ) ) {
            retVal = this.data.propertySet.propertyTypeId.get( propertyTypeId ) as PropertySet[];
        }

        return retVal;
    }

    /**
     * Returns the PropertySet for given itemType and propertyType
     *
     * @param {Number|string} itemType          The itemTypeId or itemTypeName
     * @param {Number|string} propertyType      The propertyTypeId or propertyTypeName
     */
    getPropertySetForItemTypeAndPropertyType( itemType : number | string, propertyType : number | string ) : PropertySet | undefined {
        let retVal,
            propertySets;

        const itemTypeId     = this.getItemType( itemType )?.id,
              propertyTypeId = this.getPropertyType( propertyType )?.id;

        if ( itemTypeId && propertyTypeId ) {

            // get the smaller collection
            if ( ( this.data.propertySet.itemTypeId?.size ?? 0 ) < ( this.data.propertySet.propertyTypeId?.size ?? 0 ) ) {
                propertySets = this.data.propertySet.itemTypeId!.get( itemTypeId ) as PropertySet[];
            }
            else {
                propertySets = this.data.propertySet.propertyTypeId!.get( propertyTypeId ) as PropertySet[];
            }

            if ( propertySets ) {
                for ( const propertySet of propertySets ) {
                    if ( propertySet.itemTypeId === itemTypeId && propertySet.propertyTypeId === propertyTypeId ) {
                        retVal = propertySet;
                        break;
                    }
                }
            }
        }

        return retVal;
    }


    /************************************************************
     * PropertyType
     ************************************************************/

    /**
     * Returns all PropertyTypes
     */
    getPropertyTypes() : PropertyType[] {
        return [ ...this.data.propertyType.id.values() ];
    }

    /**
     * Returns the PropertyType for given propertyType
     *
     * @param {Number|string} propertyType      The propertyTypeId or propertyTypeName
     */
    getPropertyType( propertyType : number | string ) : PropertyType | undefined {
        let retVal;

        if ( typeof propertyType === 'number' && this.data.propertyType.id.has( propertyType ) ) {
            retVal = this.data.propertyType.id.get( propertyType );
        }
        else if ( typeof propertyType === 'string' && this.data.propertyType.name?.has( propertyType ) ) {
            retVal = this.data.propertyType.name.get( propertyType );

            if ( retVal ) {
                retVal = retVal as PropertyType;
            }
        }

        return retVal;
    }


    /************************************************************
     * ReferencePropertySet
     ************************************************************/

    /**
     * Returns all ReferencePropertySets
     */
    getReferencePropertySets() : ReferencePropertySet[] {
        return [ ...this.data.referencePropertySet.id.values() ];
    }

    /**
     * Returns the ReferencePropertySet record for given referencePropertySetId
     *
     * @param {Number} referencePropertySetId
     */
    getReferencePropertySet( referencePropertySetId : number ) : ReferencePropertySet | undefined {
        let retVal;

        if ( this.data.referencePropertySet.id.has( referencePropertySetId ) ) {
            retVal = this.data.referencePropertySet.id.get( referencePropertySetId );
        }

        return retVal;
    }

    /**
     * Returns all ReferencePropertySets for given referenceSetId
     *
     * @param {Number} referenceSetId
     */
    getReferencePropertySetsForReferenceSet( referenceSetId : number ) : ReferencePropertySet[] {
        let retVal : ReferencePropertySet[] = [];

        if ( this.data.referencePropertySet.referenceSetId?.has( referenceSetId ) ) {
            retVal = this.data.referencePropertySet.referenceSetId.get( referenceSetId ) as ReferencePropertySet[];
        }

        return retVal;
    }

    /**
     * Returns all ReferencePropertySets for given propertyType
     *
     * @param {Number|string} propertyType      The propertyTypeId or propertyTypeName
     */
    getReferencePropertySetsForPropertyType( propertyType : number | string ) : ReferencePropertySet[] {
        let retVal : ReferencePropertySet[] = [];

        const propertyTypeId = this.getPropertyType( propertyType )?.id;

        if ( propertyTypeId && this.data.referencePropertySet.propertyTypeId?.has( propertyTypeId ) ) {
            retVal = this.data.referencePropertySet.propertyTypeId.get( propertyTypeId ) as ReferencePropertySet[];
        }

        return retVal;
    }

    /**
     * Returns the ReferencePropertySet for given referenceSetId and propertyType
     *
     * @param {Number} referenceSetId
     * @param {Number|string} propertyType      The propertyTypeId or propertyTypeName
     */
    getReferencePropertySetForReferenceSetAndPropertyType( referenceSetId : number, propertyType : number | string ) : ReferencePropertySet | undefined {
        let retVal,
            referencePropertySets;

        const propertyTypeId = this.getPropertyType( propertyType )?.id;

        if ( propertyTypeId ) {

            // get the smaller collection
            if ( this.data.referencePropertySet.referenceSetId!.size < this.data.referencePropertySet.propertyTypeId!.size ) {
                referencePropertySets = this.data.referencePropertySet.referenceSetId!.get( referenceSetId ) as ReferencePropertySet[];
            }
            else {
                referencePropertySets = this.data.referencePropertySet.propertyTypeId!.get( propertyTypeId ) as ReferencePropertySet[];
            }

            if ( referencePropertySets ) {
                for ( const referencePropertySet of referencePropertySets ) {
                    if ( referencePropertySet.referenceSetId === referenceSetId && referencePropertySet.propertyTypeId === propertyTypeId ) {
                        retVal = referencePropertySet;
                        break;
                    }
                }
            }
        }

        return retVal;
    }


    /************************************************************
     * ReferenceSet
     ************************************************************/

    /**
     * Returns all ReferenceSets
     */
    getReferenceSets() : ReferenceSet[] {
        return [ ...this.data.referenceSet.id.values() ];
    }

    /**
     * Returns the ReferenceSet for given referenceSetId
     *
     * @param {Number} referenceSetId
     */
    getReferenceSet( referenceSetId : number ) : ReferenceSet | undefined {
        let retVal;

        if ( this.data.referenceSet.id.has( referenceSetId ) ) {
            retVal = this.data.referenceSet.id.get( referenceSetId );
        }

        return retVal;
    }

    /**
     * Returns all ReferenceSets for given ItemType and direction
     *
     * @param {Number|string} itemType                      The itemTypeId or itemTypeName
     * @param {'both'|'src'|'dest'} [direction='both']     The reference direction viewed from given ItemType, so src means given ItemType is the source of the reference
     */
    getReferenceSetsForItemType( itemType : number | string, direction : 'src' | 'dest' | 'both' = 'both' ) : ReferenceSet[] {
        let retVal : ReferenceSet[] = [];

        const itemTypeId = this.getItemType( itemType )?.id;

        if ( itemTypeId ) {
            if ( direction !== 'dest' && this.data.referenceSet.srcItemTypeId?.has( itemTypeId ) ) {
                retVal = retVal.concat( this.data.referenceSet.srcItemTypeId.get( itemTypeId ) as ReferenceSet[] );
            }

            if ( direction !== 'src' && this.data.referenceSet.destItemTypeId?.has( itemTypeId ) ) {
                retVal = retVal.concat( this.data.referenceSet.destItemTypeId.get( itemTypeId ) as ReferenceSet[] );
            }
        }

        return retVal;
    }

    /**
     * Returns all ReferenceSets for given ReferenceType
     *
     * @param {Number|string} referenceType         The referenceTypeId or referenceTypeName
     */
    getReferenceSetsForReferenceType( referenceType : number | string ) : ReferenceSet[] {
        const referenceTypeId = this.getReferenceType( referenceType )?.id;

        let retVal : ReferenceSet[] = [];

        if ( referenceTypeId && this.data.referenceSet.referenceTypeId?.has( referenceTypeId ) ) {
            retVal = this.data.referenceSet.referenceTypeId.get( referenceTypeId ) as ReferenceSet[];
        }

        return retVal;
    }

    /**
     * Returns all ReferenceSets for given ItemType, ReferenceType and direction
     *
     * @param {Number|string} itemType                      The itemTypeId or itemTypeName
     * @param {Number|string} referenceType                 The referenceTypeId or referenceTypeName
     * @param {'both'|'src'|'dest'} [direction='both']      The reference direction viewed from given ItemType, so src means given ItemType is the source of the reference
     */
    getReferenceSetsForItemTypeAndReferenceType( itemType : number | string, referenceType : number | string, direction : 'src' | 'dest' | 'both' = 'both' ) : ReferenceSet[] {
        const retVal : ReferenceSet[] = [],
              itemTypeId              = this.getItemType( itemType )?.id,
              referenceTypeId         = this.getReferenceType( referenceType )?.id;

        let referenceSets,
            referenceSets1,
            referenceSets2;

        if ( itemTypeId && referenceTypeId ) {
            if (
                direction !== 'dest'
                && this.data.referenceSet.srcItemTypeId?.has( itemTypeId )
                && this.data.referenceSet.referenceTypeId?.has( referenceTypeId )
            ) {
                // get the smaller collection
                referenceSets1 = this.data.referenceSet.srcItemTypeId.get( itemTypeId ) as ReferenceSet[];
                referenceSets2 = this.data.referenceSet.referenceTypeId.get( referenceTypeId ) as ReferenceSet[];

                if ( referenceSets1.length < referenceSets2.length ) {
                    referenceSets = referenceSets1;
                }
                else {
                    referenceSets = referenceSets2;
                }

                if ( referenceSets ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            referenceSet.srcItemTypeId === itemTypeId
                            && referenceSet.referenceTypeId === referenceTypeId
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }

            if (
                direction !== 'src'
                && this.data.referenceSet.destItemTypeId?.has( itemTypeId )
                && this.data.referenceSet.referenceTypeId?.has( referenceTypeId )
            ) {
                // get the smaller collection
                referenceSets1 = this.data.referenceSet.destItemTypeId.get( itemTypeId ) as ReferenceSet[];
                referenceSets2 = this.data.referenceSet.referenceTypeId.get( referenceTypeId ) as ReferenceSet[];

                if ( referenceSets1.length < referenceSets2.length ) {
                    referenceSets = referenceSets1;
                }
                else {
                    referenceSets = referenceSets2;
                }

                if ( referenceSets ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            referenceSet.destItemTypeId === itemTypeId
                            && referenceSet.referenceTypeId === referenceTypeId
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Returns all ReferenceSets for both given ItemTypes and direction
     *
     * @param {Number|string} itemType1                     The itemTypeId or itemTypeName
     * @param {Number|string} itemType2                     The itemTypeId or itemTypeName
     * @param {'both'|'src'|'dest'} [direction='both']      The reference direction viewed from given ItemType1, so src means given ItemType1 is the source of the reference
     */
    getReferenceSetsForItemTypes( itemType1 : number | string, itemType2 : number | string, direction : 'src' | 'dest' | 'both' = 'both' ) : ReferenceSet[] {
        const retVal : ReferenceSet[] = [],
              itemTypeId1             = this.getItemType( itemType1 )?.id,
              itemTypeId2             = this.getItemType( itemType2 )?.id;

        let referenceSets,
            referenceSets1,
            referenceSets2;

        if ( itemTypeId1 && itemTypeId2 ) {
            if (
                direction !== 'dest'
                && this.data.referenceSet.srcItemTypeId?.has( itemTypeId1 )
                && this.data.referenceSet.destItemTypeId?.has( itemTypeId2 )
            ) {
                // get the smaller collection
                referenceSets1 = this.data.referenceSet.srcItemTypeId.get( itemTypeId1 ) as ReferenceSet[];
                referenceSets2 = this.data.referenceSet.destItemTypeId.get( itemTypeId2 ) as ReferenceSet[];

                if ( referenceSets1.length < referenceSets2.length ) {
                    referenceSets = referenceSets1;
                }
                else {
                    referenceSets = referenceSets2;
                }

                if ( referenceSets ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            referenceSet.srcItemTypeId === itemTypeId1
                            && referenceSet.destItemTypeId === itemTypeId2
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }

            if (
                direction !== 'src'
                && this.data.referenceSet.destItemTypeId?.has( itemTypeId1 )
                && this.data.referenceSet.srcItemTypeId?.has( itemTypeId2 )
            ) {
                // get the smaller collection
                referenceSets1 = this.data.referenceSet.destItemTypeId.get( itemTypeId1 ) as ReferenceSet[];
                referenceSets2 = this.data.referenceSet.srcItemTypeId.get( itemTypeId2 ) as ReferenceSet[];

                if ( referenceSets1.length < referenceSets2.length ) {
                    referenceSets = referenceSets1;
                }
                else {
                    referenceSets = referenceSets2;
                }

                if ( referenceSets ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            referenceSet.destItemTypeId === itemTypeId1
                            && referenceSet.srcItemTypeId === itemTypeId2
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }
        }

        return retVal;
    }

    /**
     * Returns all ReferenceSets for both given ItemTypes, ReferenceType and direction
     *
     * @param {Number|string} itemType1             The itemTypeId or itemTypeName
     * @param {Number|string} itemType2             The itemTypeId or itemTypeName
     * @param {Number|string} referenceType         The referenceTypeId or referenceTypeName
     * @param {'both'|'src'|'dest'} [direction='both']     The reference direction viewed from given ItemType1, so src means given ItemType1 is the source of the reference
     */
    getReferenceSetsForItemTypesAndReferenceType( itemType1 : number | string, itemType2 : number | string, referenceType : number | string, direction : 'src' | 'dest' | 'both' = 'both' ) : ReferenceSet[] {
        const retVal : ReferenceSet[] = [];

        let negateItemType1     = false,
            negateItemType2     = false,
            negateReferenceType = false;

        if ( typeof itemType1 === 'string' && itemType1.startsWith( '!' ) ) {
            negateItemType1 = true;
            itemType1 = itemType1.slice( 1 );
        }
        if ( typeof itemType2 === 'string' && itemType2.startsWith( '!' ) ) {
            negateItemType2 = true;
            itemType2 = itemType2.slice( 1 );
        }
        if ( typeof referenceType === 'string' && referenceType.startsWith( '!' ) ) {
            negateReferenceType = true;
            referenceType = referenceType.slice( 1 );
        }

        const
            itemTypeId1     = itemType1 === '*' ? 0 : this.getItemType( itemType1 )?.id,
            itemTypeId2     = itemType2 === '*' ? 0 : this.getItemType( itemType2 )?.id,
            referenceTypeId = referenceType === '*' ? 0 : this.getReferenceType( referenceType )?.id;

        let referenceSets : ReferenceSet[]          = [],
            referenceSets1 : ReferenceSet[] | false = false,
            referenceSets2 : ReferenceSet[] | false = false,
            referenceSets3 : ReferenceSet[] | false = false;

        referenceSets3 = referenceTypeId
            ? ( negateReferenceType
                    ? [ ...this.data.referenceSet.referenceTypeId! ].filter( x => x[ 1 ] !== this.data.referenceSet.referenceTypeId!.get( referenceTypeId ) ).reduce( ( accu, x ) => accu.concat( x[ 1 ] ), [] as ReferenceSet[] )
                    : this.data.referenceSet.referenceTypeId?.get( referenceTypeId ) as ReferenceSet[]
            )
            : false;

        if ( referenceTypeId !== undefined && itemTypeId1 !== undefined && itemTypeId2 !== undefined ) {
            if (
                direction !== 'dest'
                && ( !itemTypeId1 || this.data.referenceSet.srcItemTypeId?.has( itemTypeId1 ) )
                && ( !itemTypeId2 || this.data.referenceSet.destItemTypeId?.has( itemTypeId2 ) )
                && ( !referenceTypeId || this.data.referenceSet.referenceTypeId?.has( referenceTypeId ) )
            ) {
                // get the smaller collection
                referenceSets1 = itemTypeId1
                    ? ( negateItemType1
                            ? [ ...this.data.referenceSet.srcItemTypeId! ].filter( x => x[ 1 ] !== this.data.referenceSet.srcItemTypeId!.get( itemTypeId1 ) ).reduce( ( accu, x ) => accu.concat( x[ 1 ] ), [] as ReferenceSet[] )
                            : this.data.referenceSet.srcItemTypeId?.get( itemTypeId1 ) as ReferenceSet[]
                    )
                    : false;
                referenceSets2 = itemTypeId2
                    ? ( negateItemType2
                            ? [ ...this.data.referenceSet.destItemTypeId! ].filter( x => x[ 1 ] !== this.data.referenceSet.destItemTypeId!.get( itemTypeId2 ) ).reduce( ( accu, x ) => accu.concat( x[ 1 ] ), [] as ReferenceSet[] )
                            : this.data.referenceSet.destItemTypeId?.get( itemTypeId2 ) as ReferenceSet[]
                    )
                    : false;

                if ( referenceSets1 && referenceSets2 ) {
                    if ( referenceSets1.length <= referenceSets2.length && ( !referenceSets3 || referenceSets1.length < referenceSets3.length ) ) {
                        referenceSets = referenceSets1;
                    }
                    else if ( referenceSets2.length < referenceSets1.length && ( !referenceSets3 || referenceSets2.length < referenceSets3.length ) ) {
                        referenceSets = referenceSets2;
                    }
                    else if ( referenceSets3 ) {
                        referenceSets = referenceSets3;
                    }
                }
                else if ( referenceSets1 ) {
                    referenceSets = ( !referenceSets3 || referenceSets1.length < referenceSets3.length ) ? referenceSets1 : referenceSets3 || [];
                }
                else if ( referenceSets2 ) {
                    referenceSets = ( !referenceSets3 || referenceSets2.length < referenceSets3.length ) ? referenceSets2 : referenceSets3 || [];
                }
                else if ( referenceSets3 ) {
                    referenceSets = referenceSets3;
                }

                if ( !_.isEmpty( referenceSets ) ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            ( !itemTypeId1 || negateIf( referenceSet.srcItemTypeId === itemTypeId1, negateItemType1 ) )
                            && ( !itemTypeId2 || negateIf( referenceSet.destItemTypeId === itemTypeId2, negateItemType2 ) )
                            && ( !referenceTypeId || negateIf( referenceSet.referenceTypeId === referenceTypeId, negateReferenceType ) )
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }

            if (
                direction !== 'src'
                && ( !itemTypeId1 || this.data.referenceSet.destItemTypeId?.has( itemTypeId1 ) )
                && ( !itemTypeId2 || this.data.referenceSet.srcItemTypeId?.has( itemTypeId2 ) )
                && ( !referenceTypeId || this.data.referenceSet.referenceTypeId?.has( referenceTypeId ) )
            ) {
                // get the smaller collection
                referenceSets1 = itemTypeId1
                    ? ( negateItemType1
                            ? [ ...this.data.referenceSet.destItemTypeId! ].filter( x => x[ 1 ] !== this.data.referenceSet.destItemTypeId!.get( itemTypeId1 ) ).reduce( ( accu, x ) => accu.concat( x[ 1 ] ), [] as ReferenceSet[] )
                            : this.data.referenceSet.destItemTypeId?.get( itemTypeId1 ) as ReferenceSet[]
                    )
                    : false;
                referenceSets2 = itemTypeId2
                    ? ( negateItemType2
                            ? [ ...this.data.referenceSet.srcItemTypeId! ].filter( x => x[ 1 ] !== this.data.referenceSet.srcItemTypeId!.get( itemTypeId2 ) ).reduce( ( accu, x ) => accu.concat( x[ 1 ] ), [] as ReferenceSet[] )
                            : this.data.referenceSet.srcItemTypeId?.get( itemTypeId2 ) as ReferenceSet[]
                    )
                    : false;

                if ( referenceSets1 && referenceSets2 ) {
                    if ( referenceSets1.length <= referenceSets2.length && ( !referenceSets3 || referenceSets1.length < referenceSets3.length ) ) {
                        referenceSets = referenceSets1;
                    }
                    else if ( referenceSets2.length < referenceSets1.length && ( !referenceSets3 || referenceSets2.length < referenceSets3.length ) ) {
                        referenceSets = referenceSets2;
                    }
                    else if ( referenceSets3 ) {
                        referenceSets = referenceSets3;
                    }
                }
                else if ( referenceSets1 ) {
                    referenceSets = ( !referenceSets3 || referenceSets1.length < referenceSets3.length ) ? referenceSets1 : referenceSets3 || [];
                }
                else if ( referenceSets2 ) {
                    referenceSets = ( !referenceSets3 || referenceSets2.length < referenceSets3.length ) ? referenceSets2 : referenceSets3 || [];
                }
                else if ( referenceSets3 ) {
                    referenceSets = referenceSets3;
                }

                if ( referenceSets ) {
                    for ( const referenceSet of referenceSets ) {
                        if (
                            ( !itemTypeId1 || negateIf( referenceSet.destItemTypeId === itemTypeId1, negateItemType1 ) )
                            && ( !itemTypeId2 || negateIf( referenceSet.srcItemTypeId === itemTypeId2, negateItemType2 ) )
                            && ( !referenceTypeId || negateIf( referenceSet.referenceTypeId === referenceTypeId, negateReferenceType ) )
                        ) {
                            retVal.push( referenceSet );
                        }
                    }
                }
            }
        }

        return retVal;
    }


    /************************************************************
     * ReferenceType
     ************************************************************/

    /**
     * Returns all ReferenceTypes
     */
    getReferenceTypes() : ReferenceType[] {
        return [ ...this.data.referenceType.id.values() ];
    }

    /**
     * Returns the ReferenceType for given referenceType
     *
     * @param {Number|string} referenceType      The referenceTypeId or referenceTypeName
     */
    getReferenceType( referenceType : number | string ) : ReferenceType | undefined {
        let retVal;

        if ( typeof referenceType === 'number' && this.data.referenceType.id.has( referenceType ) ) {
            retVal = this.data.referenceType.id.get( referenceType );
        }
        else if ( typeof referenceType === 'string' && this.data.referenceType.name?.has( referenceType ) ) {
            retVal = this.data.referenceType.name.get( referenceType );

            if ( retVal ) {
                retVal = retVal as ReferenceType;
            }
        }

        return retVal;
    }


    /************************************************************
     * Status
     ************************************************************/

    /**
     * Returns all statuses without ATC Status
     */
    getStatuses() : Status[] {
        const retVal = this.getAllStatuses();

        let i;

        // remove ATC statuses
        for ( i = retVal.length - 1; i >= 0; i-- ) {
            if ( retVal[ i ].id > 139 && retVal[ i ].id < 150 ) {
                retVal.splice( i, 1 );
            }
        }

        return retVal;
    }

    /**
     * Returns all statuses
     */
    getAllStatuses() : Status[] {
        return [ ...this.data.status.id.values() ];
    }

    getEditableStatus() : Status[] {
        const retVal : Status[] = [],
              notSettableStatus = this.getDeletedStatus();

        for ( const value of this.data.status.id.values() ) {
            if ( !notSettableStatus.includes( value ) ) {
                retVal.push( value );
            }
        }

        return retVal;
    }

    getDeletedStatus() : Status[] {
        return [
            this.getStatus( 'history' )!,
            this.getStatus( 'deleted' )!
        ];
    }

    /**
     * Returns the status record for given status
     *
     * @param {Number|string} status      The statusId or statusName
     *
     * @return {Object}
     */
    getStatus( status : number | string ) : Status | undefined {
        let retVal;

        if ( typeof status === 'number' && this.data.status.id.has( status ) ) {
            retVal = this.data.status.id.get( status );
        }
        else if ( typeof status === 'string' && this.data.status.name?.has( status ) ) {
            retVal = this.data.status.name.get( status );

            if ( retVal ) {
                retVal = retVal as Status;
            }
        }

        return retVal;
    }


    /************************************************************
     * Value
     ************************************************************/

    /**
     * Returns the Values for the given valueSet
     *
     * @param {Number|String} valueSet
     */
    getValueSetValues( valueSet : number | string ) : Value[] {
        let retVal : Value[] = [];

        const valueSetId = this.getValueSet( valueSet )?.id;

        if ( valueSetId && this.data.value.valueSetId?.has( valueSetId ) ) {
            retVal = this.data.value.valueSetId.get( valueSetId ) as Value[];
        }

        return retVal;
    }

    /**
     * Returns the Value for the given valueSet and value
     *
     * @param {Number|String} valueSet
     * @param {String} value
     */
    getValueSetValue( valueSet : number | string, value : number | string ) : Value | undefined {
        let retVal;

        const values = this.getValueSetValues( valueSet );

        if ( values.length ) {
            for ( const v of values ) {
                if (
                    ( typeof value === 'number' && v.id === value )
                    || ( typeof value === 'string' && v.value === value )
                ) {
                    retVal = v;
                    break;
                }
            }
        }

        return retVal;
    }


    /************************************************************
     * ValueSet
     ************************************************************/

    /**
     * Returns all ValueSets
     */
    getValueSets() : ValueSet[] {
        return [ ...this.data.valueSet.id.values() ];
    }

    /**
     * Returns the ValueSet for given valueSet
     *
     * @param {Number|String} valueSet
     */
    getValueSet( valueSet : number | string ) : ValueSet | undefined {
        let retVal;

        if ( typeof valueSet === 'number' && this.data.valueSet.id.has( valueSet ) ) {
            retVal = this.data.valueSet.id.get( valueSet );
        }
        else if ( typeof valueSet === 'string' && this.data.valueSet.name?.has( valueSet ) ) {
            retVal = this.data.valueSet.name.get( valueSet );

            if ( retVal ) {
                retVal = retVal as ValueSet;
            }
        }

        return retVal;
    }

}

const metaModel = {
    CFG : new MetaModel( 'CFG' ),
    DATA: new MetaModel( 'DATA' )
};

export default metaModel;
